import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Circle, X as XIcon, RotateCcw, AlertTriangle, Ban } from "lucide-react";

/**
 * ProofChecklist — the 31-item static (22 sales + 9 payment) checklist plus
 * dynamic debtor / adjustment rows.  Renders IMMEDIATELY on date+shift
 * selection, even before a Shift Entry exists.  See:
 *   backend/app/checklist.py  →  /api/uploads/checklist
 *
 * States (per operator brief, Message 675, 2026-07-21):
 *   ✅ UPLOADED   — file bound
 *   ⬜ MISSING    — no file, no explicit action taken
 *   ⚠️ MISMATCH   — OCR read ≠ typed value (placeholder — needs OCR slice)
 *   ↻ REDO       — photo unreadable, retake requested
 *   ⊘ NIL        — explicit user action: "no txn / no slip this shift"
 *   ⊗ NOT_OP     — explicit user action: DU / mode not operational
 *
 * A blank line is NEVER inferred as NIL — the cashier must click ⊘ or ⊗.
 */

const STATE_META = {
  UPLOADED: { icon: Check, ring: "border-emerald-500 bg-emerald-50", text: "text-emerald-800", label: "Done" },
  MISSING:  { icon: Circle, ring: "border-slate-300 bg-white", text: "text-slate-500", label: "Not uploaded" },
  MISMATCH: { icon: AlertTriangle, ring: "border-amber-500 bg-amber-50", text: "text-amber-800", label: "Not matching" },
  REDO:     { icon: RotateCcw, ring: "border-red-500 bg-red-50", text: "text-red-800", label: "Redo" },
  NIL:      { icon: Ban, ring: "border-slate-400 bg-slate-100", text: "text-slate-700", label: "Nil" },
  NOT_OP:   { icon: XIcon, ring: "border-slate-500 bg-slate-100", text: "text-slate-700", label: "Not operational" },
};

const GROUP_ORDER = ["Sales", "Payments", "Debtor slips (whole day)", "Nozzle Adjustments"];

export default function ProofChecklist({ date, shiftCode, files, onBindRequest, refreshSignal }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // key of the row currently choosing a file

  async function load() {
    if (!date || !shiftCode) return;
    setBusy(true);
    try {
      const r = await api.get("/uploads/checklist", { params: { date, shift_code: shiftCode } });
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, [date, shiftCode, refreshSignal]); // eslint-disable-line

  async function mark(item, state) {
    try {
      await api.post("/uploads/checklist/mark", {
        date, shift_code: shiftCode, key: item.key, state,
        whole_day: !!item.whole_day,
      });
      toast.success(state === "NIL" ? `⊘ ${item.label} marked Nil` :
                    state === "NOT_OP" ? `⊗ ${item.label} marked Not Operational` :
                    state === "REDO" ? `↻ ${item.label} marked for Redo` :
                    "Reset");
      load();
    } catch (e) { toast.error(apiError(e)); }
  }

  async function bindFile(item, fileId) {
    try {
      await api.post("/uploads/checklist/bind", {
        date, shift_code: shiftCode, key: item.key, file_id: fileId,
        whole_day: !!item.whole_day,
      });
      toast.success(`✓ ${item.label} — bound`);
      setPickerFor(null);
      load();
      onBindRequest?.();
    } catch (e) { toast.error(apiError(e)); }
  }

  async function unbindFile(item) {
    try {
      await api.post("/uploads/checklist/unbind", {
        date, shift_code: shiftCode, key: item.key, file_id: item.file_id || "",
        whole_day: !!item.whole_day,
      });
      toast.success("Unbound — reset to Not uploaded");
      load();
      onBindRequest?.();
    } catch (e) { toast.error(apiError(e)); }
  }

  const grouped = useMemo(() => {
    const g = {};
    (data?.items || []).forEach((it) => {
      const k = it.group || "Other";
      (g[k] ||= []).push(it);
    });
    // Sub-group within Sales by subgroup
    return g;
  }, [data]);

  const counts = data?.counts;

  return (
    <Card className="p-4 border-2 border-slate-300 rounded-sm" data-testid="proof-checklist">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Proof Checklist</h2>
          <p className="text-xs text-slate-600 mt-0.5">
            Every proof this shift needs. Drop the file into a slot, or explicitly mark it ⊘ Nil / ⊗ Not operational. A blank is never assumed to be Nil.
          </p>
        </div>
        {counts && (
          <div className="text-right" data-testid="checklist-header-count">
            <div className="text-2xl font-bold text-slate-900 tabular-nums">
              Proofs {counts.done} of {counts.total}
            </div>
            {counts.outstanding > 0 && (
              <div className="text-xs font-bold text-red-700 mt-0.5">
                {counts.outstanding} still to upload / mark
              </div>
            )}
            {counts.outstanding === 0 && counts.total > 0 && (
              <div className="text-xs font-bold text-emerald-700 mt-0.5">All accounted for ✓</div>
            )}
          </div>
        )}
      </div>

      {/* Progress bar */}
      {counts && counts.total > 0 && (
        <div className="h-2 bg-slate-200 rounded-sm overflow-hidden mb-3">
          <div
            className={"h-full transition-all duration-500 " + (counts.outstanding === 0 ? "bg-emerald-600" : "bg-slate-900")}
            style={{ width: `${(counts.done / counts.total) * 100}%` }}
            data-testid="checklist-progress-bar"
          />
        </div>
      )}

      {busy && !data && <div className="text-sm text-slate-500 py-6 text-center">Loading checklist…</div>}

      {GROUP_ORDER.map((group) => {
        const items = grouped[group] || [];
        if (items.length === 0) return null;

        // Within Sales, further split into fuel DUs / CNG DUs by subgroup prefix
        const subgroups = {};
        items.forEach((it) => {
          const sg = it.subgroup || "";
          (subgroups[sg] ||= []).push(it);
        });

        const groupDone = items.filter((i) => ["UPLOADED", "NIL", "NOT_OP"].includes(i.state)).length;

        return (
          <div key={group} className="mb-4" data-testid={`checklist-group-${group.replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-1 border-b border-slate-200 pb-1">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">{group}</h3>
              <span className="text-xs font-mono text-slate-600">{groupDone}/{items.length}</span>
            </div>

            {Object.entries(subgroups).map(([sg, rows]) => (
              <div key={sg} className="mt-2">
                {group === "Sales" && (
                  <div className="text-xs font-bold text-slate-600 mb-1">{sg}</div>
                )}
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {rows.map((it) => (
                    <ChecklistRow
                      key={it.key}
                      item={it}
                      files={files}
                      pickerOpen={pickerFor === it.key}
                      onOpenPicker={() => setPickerFor(pickerFor === it.key ? null : it.key)}
                      onBind={(fid) => bindFile(it, fid)}
                      onUnbind={() => unbindFile(it)}
                      onMark={(s) => mark(it, s)}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      })}

      {counts && counts.total === 0 && (
        <div className="text-sm text-slate-600 py-4 text-center">No slots yet.</div>
      )}
    </Card>
  );
}

function ChecklistRow({ item, files, pickerOpen, onOpenPicker, onBind, onUnbind, onMark }) {
  const meta = STATE_META[item.state] || STATE_META.MISSING;
  const Icon = meta.icon;
  const boundFile = item.file_id ? files.find((f) => f.file_id === item.file_id) : null;

  // Symbol shown as a compact glyph next to the label — matches operator's
  // spec exactly.
  const glyph = {
    UPLOADED: "✅",
    MISSING:  "⬜",
    MISMATCH: "⚠️",
    REDO:     "↻",
    NIL:      "⊘",
    NOT_OP:   "⊗",
  }[item.state] || "⬜";

  return (
    <li
      className={`border-2 rounded-sm p-2 text-sm ${meta.ring} transition-colors`}
      data-testid={`checklist-item-${item.key}`}
    >
      <div className="flex items-start gap-2">
        <div className={`shrink-0 mt-0.5 ${meta.text}`} aria-hidden>
          <span className="text-base font-bold" data-testid={`state-glyph-${item.key}`}>{glyph}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 truncate" title={item.label}>{item.label}</div>
          <div className={`text-xs mt-0.5 ${meta.text} font-bold`}>{meta.label}</div>
          {boundFile && (
            <div className="text-[10px] text-slate-500 font-mono truncate mt-0.5">
              File: {boundFile.original_name || item.file_id}
            </div>
          )}
          {item.note && <div className="text-[10px] text-slate-500 italic mt-0.5">{item.note}</div>}
        </div>
        <Icon size={16} className={`shrink-0 ${meta.text}`} />
      </div>

      {/* Action row — quick actions for this line */}
      <div className="flex flex-wrap gap-1 mt-2">
        {item.state === "MISSING" && (
          <>
            <Button
              size="sm" variant="outline"
              className="text-xs h-7 border-slate-400 bg-white"
              onClick={onOpenPicker}
              data-testid={`bind-btn-${item.key}`}
            >
              Attach file
            </Button>
            <Button
              size="sm" variant="outline"
              className="text-xs h-7 border-slate-400 text-slate-700 bg-white"
              onClick={() => onMark("NIL")}
              data-testid={`nil-btn-${item.key}`}
              title="Explicitly mark as no txn / no slip this shift"
            >
              ⊘ Nil
            </Button>
            <Button
              size="sm" variant="outline"
              className="text-xs h-7 border-slate-400 text-slate-700 bg-white"
              onClick={() => onMark("NOT_OP")}
              data-testid={`notop-btn-${item.key}`}
              title="This DU / mode is not operational"
            >
              ⊗ Not op
            </Button>
          </>
        )}
        {item.state === "UPLOADED" && (
          <>
            <Button
              size="sm" variant="outline"
              className="text-xs h-7 border-amber-400 text-amber-700 bg-white"
              onClick={() => onMark("REDO")}
              data-testid={`redo-btn-${item.key}`}
            >
              ↻ Redo
            </Button>
            <Button
              size="sm" variant="outline"
              className="text-xs h-7 border-red-300 text-red-700 bg-white"
              onClick={onUnbind}
              data-testid={`unbind-btn-${item.key}`}
            >
              Unbind
            </Button>
          </>
        )}
        {(item.state === "NIL" || item.state === "NOT_OP" || item.state === "REDO") && (
          <Button
            size="sm" variant="outline"
            className="text-xs h-7 border-slate-400 bg-white"
            onClick={() => onMark("MISSING")}
            data-testid={`reset-btn-${item.key}`}
          >
            Reset
          </Button>
        )}
      </div>

      {pickerOpen && item.state === "MISSING" && (
        <div className="mt-2 border-t border-slate-200 pt-2">
          <div className="text-xs font-bold text-slate-700 mb-1">Pick a file to attach:</div>
          {files.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No files uploaded yet — drop files in the box above first.</div>
          ) : (
            <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto">
              {files.map((f) => (
                <button
                  key={f.file_id}
                  className="text-left text-xs bg-white border border-slate-300 rounded-sm p-1.5 hover:bg-slate-50 truncate"
                  onClick={() => onBind(f.file_id)}
                  data-testid={`bind-file-option-${item.key}-${f.file_id}`}
                >
                  {f.original_name || f.file_id}
                  {f.checklist_key && f.checklist_key !== item.key && (
                    <span className="text-amber-700 ml-1">· also bound to another slot</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
